// src/crm/conversations/EmailComposer.tsx
import { useState } from 'react'
import type { SendMessageInput } from './useSendMessage'
import type { Creator, Deal } from '../../lib/types'
import { TemplatePicker } from '../templates/TemplatePicker'
import { hasUnresolvedTokens } from '../templates/resolve'

type Step = 'compose' | 'preview'

type Props = {
  creatorId: string
  creatorEmail: string | null
  contact: Creator
  deal: Deal | null
  appName: string
  onSend: (input: SendMessageInput) => void
  isSending: boolean
  sendError: string | null
  onCancel: () => void
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  padding: '0.375rem 0.5rem',
  fontFamily: 'inherit',
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.8125rem',
  color: 'var(--muted)',
  fontWeight: 500,
}

export function EmailComposer({ creatorId, creatorEmail, contact, deal, appName, onSend, isSending, sendError, onCancel }: Props) {
  const [step, setStep] = useState<Step>('compose')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [missingVars, setMissingVars] = useState<string[]>([])

  // Calculé avant les DEUX return (aperçu et formulaire) : la garde vaut aussi
  // pour le bouton « Envoyer » de l'étape aperçu.
  const unresolved = hasUnresolvedTokens(subject) || hasUnresolvedTokens(body)

  function handlePreview(e: React.FormEvent) {
    e.preventDefault()
    if (unresolved) return
    if (subject.trim() && body.trim()) setStep('preview')
  }

  function handleSend() {
    onSend({
      creatorId,
      channel: 'email',
      direction: 'out',
      subject: subject.trim(),
      body: body.trim(),
      send: true,
    })
  }

  if (step === 'preview') {
    return (
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '8px',
          padding: '1.25rem',
        }}
      >
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--muted)',
            marginBottom: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
          }}
        >
          Aperçu — Email vers {creatorEmail ?? '(email inconnu)'}
        </div>

        <div
          style={{
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            padding: '0.875rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'var(--fg)',
              marginBottom: '0.375rem',
            }}
          >
            Sujet : {subject}
          </div>
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--fg)',
              whiteSpace: 'pre-wrap',
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            {body}
          </p>
        </div>

        {sendError && (
          <div
            style={{
              fontSize: '0.8125rem',
              color: 'var(--arancio)',
              marginBottom: '0.75rem',
              background: 'rgba(217,83,30,0.1)',
              border: '1px solid rgba(217,83,30,0.3)',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem',
            }}
          >
            {sendError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setStep('compose')}
            disabled={isSending}
            style={{
              padding: '0.4375rem 1rem',
              background: 'transparent',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              color: 'var(--muted)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Modifier
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending || unresolved}
            style={{
              padding: '0.4375rem 1rem',
              background: 'var(--arancio)',
              border: 'none',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: isSending ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: isSending ? 0.6 : 1,
            }}
          >
            {isSending ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={handlePreview}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '8px',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div
        style={{
          fontSize: '0.75rem',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
        }}
      >
        Rédiger un email
      </div>

      <div
        style={{
          fontSize: '0.8125rem',
          color: 'var(--dim)',
        }}
      >
        De : ugc@basaltlab.app &nbsp;·&nbsp; Reply-To : ugc@otia.studio
        {creatorEmail && <> &nbsp;·&nbsp; À : {creatorEmail}</>}
      </div>

      <TemplatePicker
        channel="email"
        contact={contact}
        deal={deal}
        appName={appName}
        onApply={({ subject: s, body: b, missing }) => {
          if (s != null) setSubject(s)
          setBody(b)
          setMissingVars(missing)
        }}
      />

      <label style={labelStyle}>
        <span>Sujet</span>
        <input
          aria-label="Sujet"
          type="text"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={inputStyle}
          placeholder="Objet de l'email"
        />
      </label>

      <label style={labelStyle}>
        <span>Corps</span>
        <textarea
          aria-label="Corps"
          required
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
          placeholder="Votre message…"
        />
      </label>

      {unresolved && (
        <p style={{ color: 'var(--arancio)', fontSize: '0.8125rem', margin: 0 }}>
          Variables non résolues restantes{missingVars.length > 0 ? ` (${missingVars.map((m) => `{${m}}`).join(', ')})` : ''} — complétez le texte avant l'envoi.
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '0.4375rem 1rem',
            background: 'transparent',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            color: 'var(--muted)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={unresolved}
          style={{
            padding: '0.4375rem 1rem',
            background: 'var(--elevated)',
            border: '1px solid var(--line-strong)',
            borderRadius: '6px',
            color: unresolved ? 'var(--dim)' : 'var(--fg)',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: unresolved ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Aperçu →
        </button>
      </div>
    </form>
  )
}
